import React, {
  Dispatch,
  ReactElement,
  SetStateAction,
  useCallback,
  useEffect, useState,
} from 'react';
import { ExpansionPanel, SearchInputButton } from 'slovlex-idsk-react-components';
import {
  Button,
  DatePicker, FormGroup, Label,
} from 'idsk-react-components';
import { PravoEuFilter } from '../../../model/PravoEuFilter';
import { SafeParameterSetter } from '../../../types/SafeParameterSetter';
import iconFilter from '../../../../assets/images/icon-filter.svg';
import './sidePanel.scss';
import TypSingleSelect from './TypSingleSelect';
import AutorSingleSelect from './AutorSingleSelect';

interface Props {
  filter: PravoEuFilter,
  setFilter: Dispatch<SetStateAction<PravoEuFilter>>,
  onUpdateField: SafeParameterSetter<PravoEuFilter>,
  filterCopy: PravoEuFilter,
  onUpdateFieldCopy: SafeParameterSetter<PravoEuFilter>,
}

const SidePanel = ({
  filter, setFilter, onUpdateField, filterCopy, onUpdateFieldCopy,
}:Props): ReactElement => {
  const isInitiallyExpanded = !!filter.datumSchvaleniaOd || !!filter.datumSchvaleniaDo
    || !!filter.datumUcinnostiOdOd || !!filter.datumUcinnostiOdDo
    || !!filter.datumUcinnostiDoOd || !!filter.datumUcinnostiDoDo
    || !!filter.datumUzavierkyOd || !!filter.datumUzavierkyDo
    || !!filter.datumTranspozicieOd || !!filter.datumTranspozicieDo;
  const [extendedFilterShown, setExtendedFilterShown] = useState(isInitiallyExpanded);

  const submitFilter = useCallback((): void => {
    setFilter(prevState => ({
      ...prevState,
      hladanyVyraz: filterCopy.hladanyVyraz,
      celex: filterCopy.celex,
      rokUverejnenia: filterCopy.rokUverejnenia,
      eurovoc: filterCopy.eurovoc,
      zmluva: filterCopy.zmluva,
      autor: filterCopy.autor,
      type: filterCopy.type,
    }));
  }, [filterCopy.autor,
    filterCopy.type,
    filterCopy.celex,
    filterCopy.eurovoc,
    filterCopy.hladanyVyraz,
    filterCopy.rokUverejnenia,
    filterCopy.zmluva,
    setFilter]);

  useEffect(() => {
    const keyDownHandler = (e): void => {
      if (e.key === 'Enter') {
        e.preventDefault();
        submitFilter();
      }
    };
    document.addEventListener('keydown', keyDownHandler);
    return () => {
      document.removeEventListener('keydown', keyDownHandler);
    };
  }, [submitFilter]);

  const onUpdateFieldType = (field: string, value: never): void => {
    setFilter({
      ...filterCopy,
      [field]: value,
    });

    if (field === 'polozkyList') {
      onUpdateField('type', value[0]);
      onUpdateFieldCopy('type', value[0]);
    }
  };

  const onUpdateFieldAutor = (field: string, value: never): void => {
    setFilter({
      ...filterCopy,
      [field]: value,
    });

    if (field === 'polozkyList') {
      onUpdateField('autor', value[0]);
      onUpdateFieldCopy('autor', value[0]);
    }
  };

  return (
    <div className="pravo-eu-list-side-panel">
      <SearchInputButton
        label="Hľadaný výraz"
        className="slovlex-idsk-side-panel-input-wrapper first-container"
        searchText={filterCopy.hladanyVyraz}
        setSearchText={(value: string) => onUpdateFieldCopy('hladanyVyraz', value)}
        onButtonSubmit={submitFilter}
        onClearSearch={() => onUpdateFieldCopy('hladanyVyraz', '')}
      />
      <SearchInputButton
        label="Celex"
        className="slovlex-idsk-side-panel-input-wrapper"
        searchText={filterCopy.celex}
        setSearchText={(value: string) => onUpdateFieldCopy('celex', value)}
        onButtonSubmit={submitFilter}
        onClearSearch={() => onUpdateFieldCopy('celex', '')}
      />
      <SearchInputButton
        label="Rok uverejnenia"
        className="slovlex-idsk-side-panel-input-wrapper"
        type="search-number"
        maxLength={4}
        searchText={filterCopy.rokUverejnenia !== undefined ? `${(filterCopy.rokUverejnenia)}` : ''}
        setSearchText={(value: string) => onUpdateFieldCopy('rokUverejnenia', stringToNumber(value))}
        onButtonSubmit={submitFilter}
        onClearSearch={() => onUpdateFieldCopy('rokUverejnenia', undefined)}
      />
      <SearchInputButton
        label="EuroVoc"
        className="slovlex-idsk-side-panel-input-wrapper"
        searchText={filterCopy.eurovoc}
        setSearchText={(value: string) => onUpdateFieldCopy('eurovoc', value)}
        onButtonSubmit={submitFilter}
        onClearSearch={() => onUpdateFieldCopy('eurovoc', '')}
      />
      <TypSingleSelect
        onUpdateField={onUpdateFieldType}
        onUpdateFieldCopy={onUpdateFieldCopy}
        filter={filter}
        filterCopy={filterCopy}
      />
      <AutorSingleSelect
        onUpdateField={onUpdateFieldAutor}
        onUpdateFieldCopy={onUpdateFieldCopy}
        filter={filter}
        filterCopy={filterCopy}
      />
      <ExpansionPanel
        title="Zmluva"
        isExpanded={filter.zmluva !== ''}
      >
        <SearchInputButton
          searchText={filterCopy.zmluva !== undefined ? `${(filterCopy.zmluva)}` : ''}
          setSearchText={(value: string) => onUpdateFieldCopy('zmluva', value)}
          onButtonSubmit={submitFilter}
          onClearSearch={() => onUpdateFieldCopy('zmluva', '')}
        />
      </ExpansionPanel>
      <ExpansionPanel
        title="Dátum uverejnenia"
        isExpanded={!!filter.datumUverejneniaOd || !!filter.datumUverejneniaDo}
      >
        <FormGroup>
          <FormGroup>
            <Label htmlFor="datumUverejneniaOd">Dátum od</Label>
            <DatePicker
              width="full"
              showIcon
              value={filter.datumUverejneniaOd}
              onChange={date => onUpdateField('datumUverejneniaOd', date)}
              strictParsing
            />
          </FormGroup>
          <FormGroup>
            <Label htmlFor="datumUverejneniaDo">Dátum do</Label>
            <DatePicker
              width="full"
              showIcon
              value={filter.datumUverejneniaDo}
              onChange={date => onUpdateField('datumUverejneniaDo', date)}
              strictParsing
            />
          </FormGroup>
        </FormGroup>
      </ExpansionPanel>
      {extendedFilterShown ? (
        <>
          <ExpansionPanel
            title="Dátum schválenia"
            isExpanded={!!filter.datumSchvaleniaOd || !!filter.datumSchvaleniaDo}
          >
            <FormGroup>
              <FormGroup>
                <Label htmlFor="datumUverejneniaOd">Dátum od</Label>
                <DatePicker
                  width="full"
                  showIcon
                  value={filter.datumSchvaleniaOd}
                  onChange={date => onUpdateField('datumSchvaleniaOd', date)}
                  strictParsing
                />
              </FormGroup>
              <FormGroup>
                <Label htmlFor="datumSchvaleniaDo">Dátum do</Label>
                <DatePicker
                  width="full"
                  showIcon
                  value={filter.datumSchvaleniaDo}
                  onChange={date => onUpdateField('datumSchvaleniaDo', date)}
                  strictParsing
                />
              </FormGroup>
            </FormGroup>
          </ExpansionPanel>
          <ExpansionPanel
            title="Dátum účinnosti od"
            isExpanded={!!filter.datumUcinnostiOdOd || !!filter.datumUcinnostiOdDo}
          >
            <FormGroup>
              <FormGroup>
                <Label htmlFor="datumUcinnostiOdOd">Dátum od</Label>
                <DatePicker
                  width="full"
                  showIcon
                  value={filter.datumUcinnostiOdOd}
                  onChange={date => onUpdateField('datumUcinnostiOdOd', date)}
                  strictParsing
                />
              </FormGroup>
              <FormGroup>
                <Label htmlFor="datumUcinnostiOdDo">Dátum do</Label>
                <DatePicker
                  width="full"
                  showIcon
                  value={filter.datumUcinnostiOdDo}
                  onChange={date => onUpdateField('datumUcinnostiOdDo', date)}
                  strictParsing
                />
              </FormGroup>
            </FormGroup>
          </ExpansionPanel>
          <ExpansionPanel
            title="Dátum účinnosti do"
            isExpanded={!!filter.datumUcinnostiDoOd || !!filter.datumUcinnostiDoDo}
          >
            <FormGroup>
              <FormGroup>
                <Label htmlFor="datumUcinnostiDoOd">Dátum od</Label>
                <DatePicker
                  width="full"
                  showIcon
                  value={filter.datumUcinnostiDoOd}
                  onChange={date => onUpdateField('datumUcinnostiDoOd', date)}
                  strictParsing
                />
              </FormGroup>
              <FormGroup>
                <Label htmlFor="datumUcinnostiDoDo">Dátum do</Label>
                <DatePicker
                  width="full"
                  showIcon
                  value={filter.datumUcinnostiDoDo}
                  onChange={date => onUpdateField('datumUcinnostiDoDo', date)}
                  strictParsing
                />
              </FormGroup>
            </FormGroup>
          </ExpansionPanel>
          <ExpansionPanel
            title="Dátum uzávierky"
            isExpanded={!!filter.datumUzavierkyOd || !!filter.datumUzavierkyDo}
          >
            <FormGroup>
              <FormGroup>
                <Label htmlFor="datumUzavierkyOd">Dátum od</Label>
                <DatePicker
                  width="full"
                  showIcon
                  value={filter.datumUzavierkyOd}
                  onChange={date => onUpdateField('datumUzavierkyOd', date)}
                  strictParsing
                />
              </FormGroup>
              <FormGroup>
                <Label htmlFor="datumUzavierkyDo">Dátum do</Label>
                <DatePicker
                  width="full"
                  showIcon
                  value={filter.datumUzavierkyDo}
                  onChange={date => onUpdateField('datumUzavierkyDo', date)}
                  strictParsing
                />
              </FormGroup>
            </FormGroup>
          </ExpansionPanel>
          <ExpansionPanel
            title="Dátum transpozície"
            isExpanded={!!filter.datumTranspozicieOd || !!filter.datumTranspozicieDo}
          >
            <FormGroup>
              <FormGroup>
                <Label htmlFor="datumTranspozicieOd">Dátum od</Label>
                <DatePicker
                  width="full"
                  showIcon
                  value={filter.datumTranspozicieOd}
                  onChange={date => onUpdateField('datumTranspozicieOd', date)}
                  strictParsing
                />
              </FormGroup>
              <FormGroup>
                <Label htmlFor="datumTranspozicieDo">Dátum do</Label>
                <DatePicker
                  name="datumTranspozicieDo"
                  width="full"
                  showIcon
                  value={filter.datumTranspozicieDo}
                  onChange={date => onUpdateField('datumTranspozicieDo', date)}
                  strictParsing
                />
              </FormGroup>
            </FormGroup>
          </ExpansionPanel>
        </>
      )
        : (
          <Button
            className="extended-filter-button"
            buttonStyle="third"
            iconBefore={iconFilter}
            onClick={(event) => {
              event.preventDefault();
              setExtendedFilterShown(true);
            }}
          > Rozšírené filtrovanie
          </Button>
        )}
    </div>
  );
};

function stringToNumber(input: string): number {
  const num = parseInt(input, 10);
  return Number.isNaN(num) ? undefined : num;
}

export default SidePanel;
